import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Plus, Zap, Scale, Clock, TrendingUp, Search, Trophy, Eye, Star, RefreshCw, Flame, Play } from 'lucide-react';
import { useAuth, API } from '../App';
import Header from '../components/layout/Header';

const VERDICT_COLORS = { Pass: '#22C55E', Conditional: '#F59E0B', Fail: '#EF4444' };

function FrayOfTheDay() {
  const navigate = useNavigate();
  const [topic, setTopic] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    API.get('/api/fray-of-the-day/topic').then(r => setTopic(r.data)).catch(() => {});
  }, []);

  const runFotd = async (quick = false) => {
    setLoading(true);
    setResult(null);
    try {
      const r = await API.post('/api/fray-of-the-day/run', { quick });
      // #1142: navigate to the results theater so FOTD looks and feels like
      // a normal fray run rather than a dashboard widget. Backend persists
      // FOTD as a regular fray row (#566) and returns the fray_id, so we
      // can route directly. Falls back to inline-expand if no fray_id is
      // returned (legacy / partial backend response).
      //
      // The route is `/fray/:frayId/results` (App.js:422), NOT
      // `/results/:id`. The original #1142 ship used the wrong path and
      // fell through to the Landing page — debate ran on the backend
      // (OpenRouter calls fired) but the user landed on `/` thinking
      // FOTD was broken. Hotfix applied here.
      if (r.data?.id) {
        navigate(`/fray/${r.data.id}/results`);
        return;
      }
      setResult(r.data);
      setExpanded(true);
    } catch (e) {
      // #1141: surface the real backend error code instead of generic
      // "Failed to run". Backend returns structured errors per #586
      // (e.g. {detail: "No OpenRouter API key configured"} for 503,
      // or {detail: {code: "no_models"}} for structured errors).
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail
                : detail?.message || detail?.code
                || 'Failed to run — try again';
      setResult({ error: msg });
    } finally {
      setLoading(false);
    }
  };

  if (!topic) return null;

  return (
    <div className="mb-8 p-4 bg-[#FF4D00]/5 border border-[#FF4D00]/20" data-testid="fotd-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Flame className="w-4 h-4 text-[#FF4D00]" />
          <span className="text-sm font-semibold font-['Barlow_Condensed'] uppercase tracking-wide">Fray of the Day</span>
        </div>
        <span className="text-[10px] font-mono text-[#A0A0A0]">{topic.date}</span>
      </div>
      <p className="text-sm text-[#F5F1E8] mb-3 font-medium">{topic.topic}</p>
      <div className="flex gap-2">
        <button
          onClick={() => runFotd(false)}
          disabled={loading}
          className="px-4 py-2 min-h-[36px] bg-[#FF4D00] text-white text-xs font-semibold flex items-center gap-2 hover:bg-[#FF4D00]/90 transition-colors disabled:opacity-50"
          data-testid="fotd-run-btn"
        >
          <Play className="w-3 h-3" /> {loading ? 'Running...' : 'Run Full Debate'}
        </button>
        <button
          onClick={() => runFotd(true)}
          disabled={loading}
          className="px-4 py-2 min-h-[36px] bg-[#1E1E1E] border border-[#2A2A2A] text-[#A0A0A0] text-xs font-semibold flex items-center gap-2 hover:border-[#FF4D00]/40 transition-colors disabled:opacity-50"
          data-testid="fotd-quick-btn"
        >
          <Zap className="w-3 h-3" /> Quick (1 round)
        </button>
      </div>
      {result && !result.error && expanded && (
        <div className="mt-4 space-y-3">
          {result.rounds?.map((round, ri) => (
            <div key={ri}>
              <div className="text-[10px] font-mono text-[#FF4D00] mb-2 uppercase tracking-wider">Round {round.round}</div>
              {round.responses?.map((resp, rri) => (
                <div key={rri} className="mb-2 p-3 bg-[#0A0A0A] border border-[#1E1E1E]">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[10px] font-mono text-[#00D4FF]">{resp.voice}</span>
                    <span className="text-[9px] font-mono text-[#555]">{resp.model?.split('/').pop()}</span>
                  </div>
                  <p className="text-xs text-[#D0D0D0] leading-relaxed">{resp.response}</p>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
      {result?.error && <p className="mt-3 text-xs text-[#A61E17]">{result.error}</p>}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [frays, setFrays] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [pendingNotifications, setPendingNotifications] = useState(null);
  const [catFightStats, setCatFightStats] = useState(null);

  useEffect(() => {
    Promise.all([
      API.get('/api/frays').then(r => setFrays(r.data.frays || [])),
      API.get('/api/dashboard/stats').then(r => setStats(r.data)),
      API.get('/api/notifications/pending').then(r => setPendingNotifications(r.data)).catch(() => {}),
      API.get('/api/dashboard/cat-fight-stats').then(r => setCatFightStats(r.data)).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-[#0F0F0F]">
      <Header />
      <div className="pt-20 pb-12 px-4 md:px-6 max-w-6xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-xl md:text-2xl font-bold font-['Barlow_Condensed']" data-testid="dashboard-title">Dashboard</h1>
            <p className="text-[#A0A0A0] text-sm mt-1">Welcome back, {user?.name}</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => navigate('/playbooks')}
              className="px-4 md:px-5 py-2 min-h-[44px] bg-[#FF4D00] text-black font-semibold text-sm flex items-center gap-2 hover:bg-[#FF4D00]/90 transition-colors"
              data-testid="new-fray-btn"
            >
              <Plus className="w-4 h-4" /> New Fray
            </button>
            <button
              onClick={() => navigate('/cat-fight')}
              className="px-4 md:px-5 py-2 min-h-[44px] bg-[#EF4444] text-white font-semibold text-sm flex items-center gap-2 hover:bg-[#EF4444]/90 transition-colors"
              data-testid="start-cat-fight-btn"
            >
              <Zap className="w-4 h-4" /> Cat Fight
            </button>
          </div>
        </div>

        {/* Pending notifications banner */}
        {pendingNotifications && pendingNotifications.total > 0 && (
          <Link to="/connections" className="block p-4 bg-[#EF4444]/10 border border-[#EF4444]/40 mb-6 hover:bg-[#EF4444]/15 transition-colors" data-testid="pending-banner">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-[#EF4444] text-white text-sm font-bold rounded-full flex items-center justify-center animate-pulse">
                  {pendingNotifications.total}
                </div>
                <div>
                  <span className="text-sm font-medium text-[#F5F5F5]">You have pending items</span>
                  <div className="text-xs text-[#A0A0A0] mt-0.5">
                    {pendingNotifications.pending_connections > 0 && (
                      <span>{pendingNotifications.pending_connections} connection request{pendingNotifications.pending_connections > 1 ? 's' : ''}</span>
                    )}
                    {pendingNotifications.pending_connections > 0 && pendingNotifications.pending_shares > 0 && <span> and </span>}
                    {pendingNotifications.pending_shares > 0 && (
                      <span>{pendingNotifications.pending_shares} shared Fray{pendingNotifications.pending_shares > 1 ? 's' : ''} waiting for you</span>
                    )}
                  </div>
                </div>
              </div>
              <span className="text-xs text-[#FF4D00] font-mono">View</span>
            </div>
          </Link>
        )}

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10" data-testid="stats-grid">
            {[
              { label: 'Total Frays', value: stats.total_frays, icon: Scale },
              { label: 'Completed', value: stats.completed_frays, icon: Zap },
              { label: 'Avg Friction', value: `${stats.avg_tension || 0}%`, icon: TrendingUp },
              { label: 'Billing Mode', value: user?.billing_mode === 'byok' ? 'BYOK' : 'Managed', icon: Clock },
            ].map((s, i) => (
              <div key={i} className="p-4 bg-[#1A1A1A] border border-[#2A2A2A]">
                <div className="flex items-center gap-2 mb-2">
                  <s.icon className="w-4 h-4 text-[#FF4D00]" />
                  <span className="text-xs text-[#A0A0A0] font-mono">{s.label}</span>
                </div>
                <span className="text-2xl font-bold font-['Barlow_Condensed']">{s.value}</span>
              </div>
            ))}
          </div>
        )}

        {/* Fray of the Day */}
        <FrayOfTheDay />

        {/* Cat Fight Creator Stats */}
        {catFightStats && catFightStats.total_published > 0 && (
          <div className="mb-8 p-4 bg-[#EF4444]/5 border border-[#EF4444]/20" data-testid="cat-fight-stats">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Trophy className="w-4 h-4 text-[#EF4444]" />
                <span className="text-sm font-semibold font-['Barlow_Condensed']">Your Cat Fight Stats</span>
              </div>
              <Link to="/gallery/cat-fights" className="text-[10px] font-mono text-[#EF4444] hover:underline">Gallery →</Link>
            </div>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
              <div className="text-center">
                <span className="text-xl font-bold font-['Barlow_Condensed'] block">{catFightStats.total_published}</span>
                <span className="text-[9px] font-mono text-[#A0A0A0]">Published</span>
              </div>
              <div className="text-center">
                <span className="text-xl font-bold font-['Barlow_Condensed'] block flex items-center justify-center gap-1">
                  <Eye className="w-4 h-4 text-[#A0A0A0]" /> {catFightStats.total_views}
                </span>
                <span className="text-[9px] font-mono text-[#A0A0A0]">Total Views</span>
              </div>
              <div className="text-center">
                <span className="text-xl font-bold font-['Barlow_Condensed'] block flex items-center justify-center gap-1">
                  <Star className="w-4 h-4 text-[#F59E0B]" /> {catFightStats.avg_rating ? catFightStats.avg_rating.toFixed(1) : '—'}
                </span>
                <span className="text-[9px] font-mono text-[#A0A0A0]">Avg Rating</span>
              </div>
              <div className="text-center">
                <span className="text-xl font-bold font-['Barlow_Condensed'] block flex items-center justify-center gap-1">
                  <RefreshCw className="w-4 h-4 text-[#A0A0A0]" /> {catFightStats.total_reruns}
                </span>
                <span className="text-[9px] font-mono text-[#A0A0A0]">Re-runs</span>
              </div>
            </div>
            {catFightStats.hall_of_fame_count > 0 && (
              <div className="mt-3 text-center text-[10px] font-mono text-[#F59E0B]">
                🏆 {catFightStats.hall_of_fame_count} Hall of Fame badge{catFightStats.hall_of_fame_count > 1 ? 's' : ''}
              </div>
            )}
          </div>
        )}

        {/* BYOK Notice */}
        {user?.billing_mode !== 'byok' && !user?.has_openrouter_key && (
          <div className="p-4 bg-[#FF4D00]/5 border border-[#FF4D00]/20 mb-8 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid="byok-notice">
            <div>
              <p className="text-sm font-medium">Set up your OpenRouter key to run Frays</p>
              <p className="text-xs text-[#A0A0A0] mt-1">Go to Settings to paste your OpenRouter API key</p>
            </div>
            <Link to="/settings" className="px-4 py-2 min-h-[44px] bg-[#FF4D00] text-black text-sm font-medium flex items-center justify-center shrink-0">Setup BYOK</Link>
          </div>
        )}

        {/* Frays list */}
        <div>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
            <h2 className="text-lg font-semibold font-['Barlow_Condensed']">Your Frays</h2>
            <div className="relative w-full sm:w-64">
              <Search className="w-3.5 h-3.5 text-[#A0A0A0] absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={searchQuery}
                onChange={e => {
                  setSearchQuery(e.target.value);
                  if (e.target.value.length >= 2) {
                    API.get(`/api/frays/search?q=${encodeURIComponent(e.target.value)}`)
                      .then(r => setSearchResults(r.data.frays))
                      .catch(() => setSearchResults(null));
                  } else {
                    setSearchResults(null);
                  }
                }}
                className="w-full pl-9 pr-3 py-1.5 bg-[#1A1A1A] border border-[#2A2A2A] text-xs text-[#F5F5F5] focus:outline-none focus:border-[#FF4D00]"
                placeholder="Search frays..."
                data-testid="fray-search"
              />
            </div>
          </div>
          {searchResults && searchQuery.length >= 2 && (
            <p className="text-[10px] font-mono text-[#FF4D00] mb-3">{searchResults.length} results for "{searchQuery}"</p>
          )}
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map(i => <div key={i} className="h-20 animate-shimmer" />)}
            </div>
          ) : frays.length === 0 && !searchResults ? (
            <div className="text-center py-16 border border-[#2A2A2A]" data-testid="empty-state">
              <Scale className="w-10 h-10 text-[#2A2A2A] mx-auto mb-4" />
              <p className="text-[#A0A0A0] mb-4">No Frays yet. Run your first one.</p>
              <button onClick={() => navigate('/playbooks')} className="px-6 py-2 bg-[#FF4D00] text-black text-sm font-medium">
                Browse Playbooks
              </button>
            </div>
          ) : (
            <div className="space-y-3" data-testid="fray-list">
              {(searchResults || frays).map((fray, i) => (
                <motion.div
                  key={fray.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }}
                >
                  <Link
                    to={fray.status === 'completed' || fray.status === 'stopped' ? `/fray/${fray.id}/results` : `/fray/${fray.id}`}
                    className="block p-4 bg-[#1A1A1A] border border-[#2A2A2A] hover:border-[#FF4D00]/30 transition-all group"
                    data-testid={`fray-item-${fray.id}`}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 md:gap-3 mb-1 flex-wrap">
                          <span className="text-xs font-mono text-[#A0A0A0]">{fray.template_name}</span>
                          <span className={`text-[10px] px-1.5 py-0.5 font-mono border ${
                            fray.status === 'completed' ? 'text-[#22C55E] border-[#22C55E]/30' :
                            fray.status === 'running' ? 'text-[#FF4D00] border-[#FF4D00]/30' :
                            fray.status === 'stopped' ? 'text-[#A0A0A0] border-[#A0A0A0]/30' :
                            'text-[#A0A0A0] border-[#2A2A2A]'
                          }`}>{fray.status.toUpperCase()}</span>
                        </div>
                        <p className="text-sm truncate text-[#F5F5F5] group-hover:text-[#FF4D00] transition-colors">{fray.scenario}</p>
                      </div>
                      <div className="flex items-center gap-3 md:gap-4 sm:ml-4 shrink-0">
                        {fray.verdict && (
                          <span className="text-xs font-mono px-2 py-0.5 border" style={{
                            color: VERDICT_COLORS[fray.verdict.verdict] || '#A0A0A0',
                            borderColor: (VERDICT_COLORS[fray.verdict.verdict] || '#A0A0A0') + '40',
                          }}>
                            {fray.verdict.verdict}
                          </span>
                        )}
                        {fray.tension_score > 0 && (
                          <span className="text-xs font-mono text-[#FF4D00]">{fray.tension_score}%</span>
                        )}
                        <span className="text-[10px] text-[#A0A0A0] font-mono">
                          {new Date(fray.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
